import { ActionIcon, Box, Flex, TextInput } from '@mantine/core';
import { IconArrowUp, IconX } from '@tabler/icons-react';
import { LexicalEditor } from 'lexical';
import { useEffect, useState } from 'react';
import { LoadingAnimation } from 'src/UI/app-container/chat/components/LoadingAnimation';
import { SiestaBackground } from 'src/UI/common/SiestaBackground';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TableSelectionInfo } from '../types';
import {
  SIESTA_CANCEL_INLINE_EDIT_COMMAND,
  SIESTA_SELECTION_REQUEST_COMMAND,
} from './SiestaInlinePlugin';

type Props = {
  editor: LexicalEditor;
  onLoadingChange?: (isLoading: boolean) => void;
  isLoading?: boolean;
  tableSelectionInfo?: TableSelectionInfo | null;
};

export function SiestaInlineInput({
  editor,
  onLoadingChange,
  isLoading,
  tableSelectionInfo,
}: Props) {
  const [value, setValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const borderColor = isFocused ? 'var(--mantine-color-orange-4)' : 'var(--mantine-color-gray-3)';

  useEffect(() => {
    if (!isLoading) {
      setIsProcessing(false);
    }
  }, [isLoading]);

  const handleSubmit = () => {
    if (value.trim() && !isProcessing) {
      const toolbar = document.querySelector('[data-floating-toolbar]');
      const rect = toolbar?.getBoundingClientRect();
      const toolbarTopPercent = rect
        ? ((rect.top + rect.height / 2) / window.innerHeight) * 100
        : 50;

      setIsProcessing(true);
      onLoadingChange?.(true);
      editor.dispatchCommand(SIESTA_SELECTION_REQUEST_COMMAND, {
        promptText: value,
        toolbarTopPercent,
        tableSelectionInfo: tableSelectionInfo ?? undefined,
      });
      setValue('');
    }
  };

  if (isLoading) {
    return (
      <Box
        pos="relative"
        style={{
          height: 36,
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SiestaBackground />
        <Flex align="center" h={12} mt="-xs" pos="relative" style={{ zIndex: 2 }}>
          <LoadingAnimation color="white" size={12} />
        </Flex>
        <ActionIcon
          variant="transparent"
          color="white"
          size="sm"
          aria-label="Cancel edit"
          onClick={() => editor.dispatchCommand(SIESTA_CANCEL_INLINE_EDIT_COMMAND, undefined)}
          pos="absolute"
          right={8}
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <IconX size={16} />
        </ActionIcon>
      </Box>
    );
  }

  return (
    <TextInput
      value={value}
      onChange={e => setValue(e.currentTarget.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={e => {
        if (e.key === 'Enter' && !isProcessing) {
          e.preventDefault();
          handleSubmit();
        }
      }}
      disabled={isProcessing}
      placeholder="Ask Siesta AI to help"
      styles={{
        input: {
          height: 36,
          paddingLeft: 'var(--mantine-spacing-xs)',
          paddingRight: 'var(--mantine-spacing-xxs)',
          borderRadius: 16,
          border: `1px solid ${borderColor}`,
          backgroundColor: 'white',
          fontSize: 'var(--mantine-font-size-sm)',
          fontWeight: FONT_WEIGHTS.medium,
          color: 'var(--mantine-color-gray-10)',
          '&::placeholder': {
            color: 'var(--mantine-color-gray-3)',
          },
        },
      }}
      rightSection={
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={handleSubmit}
          disabled={!value.trim() || isProcessing}
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: borderColor,
            border: 'none',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: value.trim() && !isProcessing ? 'pointer' : 'default',
          }}
        >
          <IconArrowUp size={12} color="white" />
        </button>
      }
      rightSectionPointerEvents="auto"
    />
  );
}
